<script setup lang="ts">
import { useI18n } from 'vue-i18n'

// Title row above one card of the grouped todo list (iOS "inset grouped" style): color dot, tag name, the count and a
// chevron. Tapping the name folds the group, tapping the dot opens the tag's page (two buttons side by side, a button
// cannot sit inside a button). The group without a tag has no page, so its dot is only decoration.
const { t } = useI18n()
defineProps<{
  name: string | null
  color: string | null
  count: number
  expanded: boolean
}>()
defineEmits<{ toggle: []; openTag: [] }>()
</script>

<template>
  <div class="group-header">
    <button
      v-if="name !== null"
      type="button"
      class="dot-button"
      :aria-label="t('list.openTagPage', { tag: name })"
      @click="$emit('openTag')"
    >
      <span class="dot" :style="color ? { background: color } : undefined" />
    </button>
    <span v-else class="dot-button" aria-hidden="true"><span class="dot empty" /></span>
    <button
      type="button"
      class="toggle"
      :aria-expanded="expanded"
      :aria-label="t('list.groupToggle')"
      @click="$emit('toggle')"
    >
      <span class="name" :class="{ muted: name === null }">{{ name ?? t('list.noTag') }}</span>
      <span class="count">{{ count }}</span>
      <span class="chevron" :class="{ open: expanded }" aria-hidden="true">›</span>
    </button>
  </div>
</template>

<style scoped>
.group-header {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--tg-section-header-text-color);
}

.dot-button,
.toggle {
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
}

/* (a finger-sized tap area around the small dot; the dot stays where it was) */
.dot-button {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-left: -0.8rem;
  padding: 0;
}

button.dot-button {
  cursor: pointer;
}

.toggle {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  min-height: 44px;
  padding: 0.5rem 0.25rem 0.3rem 0;
  cursor: pointer;
}

.dot {
  flex: none;
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  background: var(--tg-hint-color);
}

.dot.empty {
  background: none;
  box-shadow: inset 0 0 0 1.5px var(--tg-hint-color);
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name.muted {
  color: var(--tg-hint-color);
}

.count {
  font-weight: 400;
  color: var(--tg-hint-color);
}

.chevron {
  flex: none;
  font-size: 1.2rem;
  line-height: 1;
  color: var(--tg-hint-color);
  transition: transform 0.15s ease;
}

.chevron.open {
  transform: rotate(90deg);
}
</style>
